import React, { useContext, useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import {
  convertGramsToSmallUnits,
  convertGramsToLargeUnits,
  weightSmallUnitLabel,
  weightLargeUnitLabel,
} from '../../utils/unitConversion';
import WebAppContext from '../../utils/webAppContext';
import { useCan, Privilege } from '@feedflo/access-control';
import dayjs from 'dayjs';
import {
  DATE_FORMAT_MONTH_DAY_YEAR,
  DATE_TIME_FORMAT_FULL,
  DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE,
  TIME_FORMAT_HOURS_MINUTES,
  secondsToDuration,
} from '../../utils/dates';
import Card from '../../atoms/Card';
import SensorChart from '../../organisms/DeviceCard/SensorDataChart';
import Dialog from '../../atoms/Dialog';
import Button from '../../atoms/Button';
import Select from '../../atoms/Select';
import TextArea from '../../atoms/TextArea';
import * as Sentry from '@sentry/react';

import './FeedFramePage.scss';
import CopyButton from '../../atoms/CopyButton';
import {
  GetFeedFrameCategoriesQuery,
  DeleteFeedFrameMutation,
  CreateFeedFrameNoteMutation,
  UndeleteFeedFrameMutation,
  FEED_FRAME_GQL,
} from './queries';

const SENSOR_DATA_MARGIN_SECONDS = 5 * 60;

const asHasuraUserRole = {
  headers: {
    'x-hasura-role': 'user',
  },
};

function FeedFramePage() {
  const { id } = useParams();
  const { isMetric } = useContext(WebAppContext);

  // --- State hooks ---
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddNoteDialog, setShowAddNoteDialog] = useState(false);
  const [newNoteCategory, setNewNoteCategory] = useState('');
  const [newNote, setNewNote] = useState('');

  // --- Atoms and other Hooks ---
  const { canAnywhere, loading: privilegesLoading } = useCan();
  const deleteDialogRef = useRef(null);
  const addNoteDialogRef = useRef(null);

  // --- Apollo/GraphQL hooks ---
  const { loading, data, error, refetch } = useQuery(FEED_FRAME_GQL, {
    variables: {
      id: id,
    },
    context: asHasuraUserRole,
  });

  const hasStaffAccess = canAnywhere('feature', Privilege.FEATURE_STAFF_ACCESS);
  const { data: categoriesData, loading: categoriesLoading } = useQuery(GetFeedFrameCategoriesQuery, {
    skip: !hasStaffAccess,
    context: asHasuraUserRole,
  });

  const [deleteFeedFrame, { loading: deleting }] = useMutation(DeleteFeedFrameMutation, {
    context: asHasuraUserRole,
  });
  const [undeleteFeedFrame, { loading: undeleting }] = useMutation(UndeleteFeedFrameMutation, {
    context: asHasuraUserRole,
  });
  const [createNote, { loading: creatingNote }] = useMutation(CreateFeedFrameNoteMutation, {
    context: asHasuraUserRole,
  });

  // --- Navigation/side effects ---
  useEffect(() => {
    if (showDeleteDialog && deleteDialogRef.current) {
      deleteDialogRef.current.showModal();
    } else if (!showDeleteDialog && deleteDialogRef.current) {
      deleteDialogRef.current.close();
    }
  }, [showDeleteDialog]);

  useEffect(() => {
    if (showAddNoteDialog && addNoteDialogRef.current) {
      addNoteDialogRef.current.showModal();
    } else if (!showAddNoteDialog && addNoteDialogRef.current) {
      addNoteDialogRef.current.close();
    }
  }, [showAddNoteDialog]);

  // --- Function definitions ---
  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;

    const deletedAt = Math.floor(Date.now() / 1000);

    try {
      await deleteFeedFrame({
        variables: {
          feedFrameId: id,
          deletedAt,
        },
        context: asHasuraUserRole,
      });

      setShowDeleteDialog(false);
      refetch({ context: asHasuraUserRole });
    } catch (err) {
      console.error('Error deleting feed frame:', err);
      Sentry.captureException(err);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
  };

  const handleAddNoteClick = () => {
    setShowAddNoteDialog(true);
    setNewNoteCategory('');
    setNewNote('');
  };

  const handleAddNoteCancel = () => {
    setShowAddNoteDialog(false);
    setNewNoteCategory('');
    setNewNote('');
  };

  const handleAddNoteConfirm = async () => {
    if (!newNoteCategory || !id) return;

    try {
      await createNote({
        variables: {
          object: {
            feed_frame_id: id,
            feed_frame_category_id: newNoteCategory,
            note: newNote || null,
          },
        },
        context: asHasuraUserRole,
      });

      setShowAddNoteDialog(false);
      setNewNoteCategory('');
      setNewNote('');
      refetch({ context: asHasuraUserRole });
    } catch (err) {
      console.error('Error creating feed frame note:', err);
      Sentry.captureException(err);
    }
  };

  const handleUndelete = async () => {
    if (!id) return;

    try {
      await undeleteFeedFrame({
        variables: {
          feedFrameId: id,
        },
        context: asHasuraUserRole,
      });

      refetch({ context: asHasuraUserRole });
    } catch (err) {
      console.error('Error undeleting feed frame:', err);
      Sentry.captureException(err);
    }
  };

  if (error) {
    return (
      <>
        <h1>Feed Frame Page </h1>
        <h2>Error</h2>
        <code>{JSON.stringify(error, null, '\t')}</code>
      </>
    );
  }

  if (loading || privilegesLoading) {
    return (
      <>
        <h1>Feed Frame Page </h1>
        <h2>Loading</h2>
      </>
    );
  }

  // Staff-only access check
  if (!hasStaffAccess) {
    return (
      <div className="FeedFramePage">
        <h1>Feed Frame Page</h1>
        <h2>Access Denied</h2>
        <p>This page is only available to FeedFlo staff members.</p>
      </div>
    );
  }

  const ff = data?.feed_frame_by_pk;
  const isDeleted = ff?.deleted_at !== null;
  const canDelete = hasStaffAccess && !isDeleted && ff;
  const canUndelete = hasStaffAccess && isDeleted && ff;
  const durationText = secondsToDuration(ff?.ended_at - ff?.started_at);
  const durationMinutes = (ff?.ended_at - ff?.started_at) / 60.0;

  // Effective algorithm version: device override > barn override > device default
  const deviceEffectiveAlgorithmVersion = ff?.device?.algorithm_version;

  const categoryOptions = (categoriesData?.feed_frame_category || []).map((cat) => ({
    id: cat.id,
    name: cat.description || cat.name,
    value: cat.id,
  }));

  const latestCurrentAnalysis = ff?.feed_frame_analyses.filter(ffa => ffa.algorithm_version === deviceEffectiveAlgorithmVersion)[0];

  return (
    <div className="FeedFramePage">
      <h1>
        Feed Frame @ {ff?.feed_line?.farm?.name} : {ff?.feed_line?.name}
      </h1>
      <b>ID:</b>
      {ff?.id}
      <CopyButton data={ff?.id} />
      <br />
      <b>Latest Error:</b>{' '}
      {ff?.feed_frame_truths[0]?.truth_mass_in_grams == 0
        ? 'div By 0'
        : (
          (ff?.feed_frame_analyses[0]?.latest_estimated_mass_moved_in_grams /
            ff?.feed_frame_truths[0]?.truth_mass_in_grams -
            1) *
          100
        ).toFixed(2)}
      %
      <br />
      <b>Latest Mass Moved:</b>{' '}
      {convertGramsToSmallUnits(isMetric, latestCurrentAnalysis?.latest_estimated_mass_moved_in_grams)}{' '}
      {weightSmallUnitLabel(isMetric)} (
      {convertGramsToLargeUnits(isMetric, latestCurrentAnalysis?.latest_estimated_mass_moved_in_grams, 2)}{' '}
      {weightLargeUnitLabel(isMetric)})
      <br />
      <b>Latest Flow Rate:</b>{' '}
      {convertGramsToSmallUnits(
        isMetric,
        latestCurrentAnalysis?.latest_estimated_mass_moved_in_grams / durationMinutes,
      )}{' '}
      {weightSmallUnitLabel(isMetric)}/min
      <br />
      <b>Truth Mass:</b> {convertGramsToSmallUnits(isMetric, ff?.feed_frame_truths[0]?.truth_mass_in_grams)}{' '}
      {weightSmallUnitLabel(isMetric)}
      <br />
      <b>Truth Flow Rate:</b>{' '}
      {convertGramsToSmallUnits(isMetric, ff?.feed_frame_truths[0]?.truth_mass_in_grams / durationMinutes)}{' '}
      {weightSmallUnitLabel(isMetric)}/min
      <br />
      <br />
      <b>Duration:</b> {durationText}
      <br />
      <b>Started At:</b>{' '}
      {dayjs.tz(ff?.started_at * 1000).format(DATE_FORMAT_MONTH_DAY_YEAR + ' ' + TIME_FORMAT_HOURS_MINUTES)}{' '}
      <CopyButton data={ff?.started_at} />
      <br />
      <b>Ended At:</b>{' '}
      {dayjs.tz(ff?.ended_at * 1000).format(DATE_FORMAT_MONTH_DAY_YEAR + ' ' + TIME_FORMAT_HOURS_MINUTES)}{' '}
      <CopyButton data={ff?.ended_at} />
      <br />
      <b>Created At:</b>{' '}
      {dayjs.tz(ff?.created_at * 1000).format(DATE_FORMAT_MONTH_DAY_YEAR + ' ' + TIME_FORMAT_HOURS_MINUTES)}{' '}
      <CopyButton data={ff?.created_at} />
      <br />
      <b>Deleted At :</b>{' '}
      {ff?.deleted_at
        ? dayjs.tz(ff?.deleted_at * 1000).format(DATE_FORMAT_MONTH_DAY_YEAR + ' ' + TIME_FORMAT_HOURS_MINUTES)
        : 'N/A'}
      {canDelete && (
        <>
          <Button onClick={handleDeleteClick} className="FeedFramePage-deleteButton" content="Delete FeedFrame" />
        </>
      )}
      {canUndelete && (
        <>
          {' '}
          <Button
            onClick={handleUndelete}
            className="FeedFramePage-undeleteButton"
            content="Undelete FeedFrame"
            disabled={undeleting}
          />
        </>
      )}
      <br />
      <br />
      <h2>Notes</h2>
      {ff?.feed_frame_notes && ff.feed_frame_notes.length > 0 ? (
        ff.feed_frame_notes.map((note) => (
          <Card key={note.id} className="FeedFramePage-noteCard">
            <b>Category:</b> {note.feed_frame_category?.description || note.feed_frame_category?.name}
            <br />
            <b>Note:</b> {note.note || '-'}
            <br />
            <b>Created At:</b>{' '}
            {dayjs.tz(note.created_at * 1000).format(DATE_FORMAT_MONTH_DAY_YEAR + ' ' + TIME_FORMAT_HOURS_MINUTES)}
            <br />
            <b>Created By:</b> {note?.user ? `${note.user?.name} (${note.user?.email})` : 'Unknown'}
          </Card>
        ))
      ) : (
        <p>No notes for this FeedFrame.</p>
      )}
      {hasStaffAccess && (
        <Button onClick={handleAddNoteClick} className="FeedFramePage-addNoteButton" content="Add Note" />
      )}
      <br />
      <br />
      <b>Device ID:</b> {ff?.device.id}
      <br />
      <b>Device Serial:</b>{' '}
      <Link to={`/devices?name=Default+List&sort=nameAsc&d=${ff?.device.serial}`}>{ff?.device.serial} 🔗</Link>
      <br />
      <b>Device HW:</b> {ff?.device.hw_version}
      <br />
      <b>Device algorithm version (effective):</b> {deviceEffectiveAlgorithmVersion ?? 'N/A'}
      <br />
      <b>Bin Names:</b> {ff?.feed_line?.bin_set?.bins.map((x) => x.name).join(',')}
      <br />
      <Link to={`/rawData/${ff?.device.id}?from=${ff?.started_at}&to=${ff?.ended_at}`}>📈 Full Screen Raw Data</Link>
      <SensorChart
        deviceId={ff?.device.id}
        from={dayjs.tz(1000 * (ff?.started_at - SENSOR_DATA_MARGIN_SECONDS))}
        to={dayjs.tz(1000 * (ff?.ended_at + SENSOR_DATA_MARGIN_SECONDS))}
      ></SensorChart>
      <h2>Analyses</h2>
      {ff?.feed_frame_analyses.map((ffa) => {
        const isMatchingAlgorithm = ffa.algorithm_version === deviceEffectiveAlgorithmVersion;
        return (
          <Card key={ffa.id}>
            <h4>Feed Frame Analysis:{isMatchingAlgorithm ? ' ⭐' : ''}</h4>
            <b>Uncalibrated Mass Moved</b> {convertGramsToSmallUnits(isMetric, ffa.mass_moved_in_grams)}
            {weightSmallUnitLabel(isMetric)}
            <br />
            <b>Calibrated Mass Moved</b> {convertGramsToSmallUnits(isMetric, ffa.latest_estimated_mass_moved_in_grams)}
            {weightSmallUnitLabel(isMetric)}
            <br />
            <b>Algorithm Version (SHA)</b> {ffa.algorithm_version}
            {isMatchingAlgorithm ? ' ⭐' : ''}
            <br />
            <b>CreatedAt</b> {dayjs.tz(ffa.created_at * 1000).format(DATE_TIME_FORMAT_FULL)}
            <br />
            <b>
              <u>Stats</u>
            </b>
            <br />
            <div className="FeedFramePage-stats">
              <b>Average Rotational Speed</b>{' '}
              <span>{Number(ffa.extra_data?.rot_speed_avg).toFixed(2).padStart(5, '0')}dHz</span>
              <b>Median Rotational Speed</b>{' '}
              <span>{Number(ffa.extra_data?.rot_speed_median).toFixed(2).padStart(5, '0')}dHz</span>
              <b>Average Feed Height</b>{' '}
              <span>{Number(ffa.extra_data?.feed_height_avg).toFixed(2).padStart(5, '0')}mm (note: with bonus)</span>
              <b>Median Feed Height</b>{' '}
              <span>{Number(ffa.extra_data?.feed_height_median).toFixed(2).padStart(5, '0')}mm (note: with bonus)</span>
              <b>Standard Deviation Feed Height</b>
              <span>
                {Number(ffa.extra_data?.feed_height_std_dev).toFixed(2).padStart(5, '0')}mm (note: with bonus)
              </span>
              <b>Movement Score Threshold</b>{' '}
              <span>
                {ffa.extra_data?.movement_score_threshold != null
                  ? Number(ffa.extra_data.movement_score_threshold).toFixed(2)
                  : 'N/A'}
              </span>
            </div>
            <b>Calibrations:</b>
            {ffa.feed_frame_corrected_analyses.map((ffca) => {
              return (
                <Card key={ffca.id}>
                  <h4>Corrected Feed Frame Analysis:</h4>
                  <b>ID: </b> {ffca.id}
                  <br />
                  <b>Created at: </b>
                  {dayjs.tz(ffca.created_at * 1000).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE)}
                  <br />
                  <b>Deleted at: </b>
                  {ffca.deleted_at
                    ? dayjs.tz(ffca.deleted_at * 1000).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE)
                    : 'Null'}
                  <br />
                  <b>Corrected Mass </b> {convertGramsToSmallUnits(isMetric, ffca.corrected_mass_moved_in_grams)}
                  {weightSmallUnitLabel(isMetric)}
                  <br />
                  <hr></hr>
                  <h4>Coefficient:</h4> {ffca.feed_line_coefficients.id}{' '}
                  <CopyButton data={ffca.feed_line_coefficients.id} />
                  <pre>{JSON.stringify(ffca.feed_line_coefficients.coefficients, null, 4)}</pre>
                  <br />
                  <b>Created at: </b>
                  {dayjs
                    .tz(ffca.feed_line_coefficients.created_at * 1000)
                    .format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE)}
                  <br />
                  <b>Deleted at: </b>
                  {ffca.feed_line_coefficients.deleted_at
                    ? dayjs
                      .tz(ffca.feed_line_coefficients.deleted_at * 1000)
                      .format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE)
                    : 'Null'}
                  <br />
                  <b>Applied at: Start - End - Live End</b>
                  <br></br>
                  {dayjs
                    .tz(ffca.feed_line_coefficients.started_at * 1000)
                    .format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE)}
                  {' - '}
                  {ffca.feed_line_coefficients.ended_at
                    ? dayjs
                      .tz(ffca.feed_line_coefficients.ended_at * 1000)
                      .format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE)
                    : 'Now'}
                  {' - '}
                  {ffca.feed_line_coefficients.ended_at
                    ? dayjs
                      .tz(ffca.feed_line_coefficients.live_ended_at * 1000)
                      .format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE)
                    : 'Now'}
                </Card>
              );
            })}
          </Card>
        );
      })}
      <pre>{JSON.stringify(data, null, 4)}</pre>
      <pre>{JSON.stringify(error, null, 4)}</pre>
      <Dialog ref={deleteDialogRef} className="FeedFramePage-deleteFeedframeDialog">
        <div className="FeedFramePage-deleteDialogContent">
          <h2>Delete FeedFrame</h2>
          <p>Are you sure you want to delete this FeedFrame?</p>
          <div className="FeedFramePage-dialogActions">
            <Button onClick={handleDeleteCancel} disabled={deleting} content="Cancel" />
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="FeedFramePage-deleteConfirmButton"
              content={deleting ? 'Deleting...' : 'Delete'}
            />
          </div>
        </div>
      </Dialog>
      <Dialog ref={addNoteDialogRef} className="FeedFramePage-addNoteDialog">
        <div className="FeedFramePage-deleteDialogContent">
          <h2>Add Note to FeedFrame</h2>
          <p>Select a category and optionally add a note:</p>
          <Select
            label="Category"
            itemList={categoryOptions}
            value={newNoteCategory}
            onChange={(e) => setNewNoteCategory(e.target.value)}
            loading={categoriesLoading}
            defaultText="Select a category..."
            isValid={!!newNoteCategory}
          />
          <TextArea label="Note (Optional)" value={newNote} onChange={(e) => setNewNote(e.target.value)} rows={4} />
          <div className="FeedFramePage-dialogActions">
            <Button onClick={handleAddNoteCancel} disabled={creatingNote} content="Cancel" />
            <Button
              onClick={handleAddNoteConfirm}
              disabled={!newNoteCategory || creatingNote}
              className="FeedFramePage-addNoteConfirmButton"
              content={creatingNote ? 'Adding...' : 'Add Note'}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}

FeedFramePage.propTypes = {
  testCase: PropTypes.object,
  testCaseMetaData: PropTypes.object,
  target_error_value: PropTypes.number,
};

export default FeedFramePage;
